import { FC } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, darken, lighten, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ExpandMore, LockIcon } from "../../../assets/icons";

interface Props {
	/**
	 * The names of the page contents hidden because the user is not allowed to load them.
	 */
	items: string[];
}

/**
 * ContentLocked informs the user that some contents of the page are hidden for lack of permissions;
 * the accordion details list the hidden contents. Nothing is rendered if there are no hidden contents.
 *
 * @param items the names of the hidden contents
 * @returns     the content locked banner
 */
const ContentLocked: FC<Props> = ({ items }) => {
	const { t } = useTranslation("common");

	if (items.length === 0) {
		return null;
	}

	return (
		<Accordion
			disableGutters
			sx={(theme) => {
				const isLight = theme.palette.mode === "light";
				const info = theme.palette.info.main;

				return {
					backgroundColor: isLight ? lighten(info, 0.9) : darken(info, 0.9),
					color: isLight ? darken(info, 0.6) : lighten(info, 0.6),
					borderRadius: 2,
					boxShadow: "none",
					"&::before": { display: "none" },
				};
			}}
		>
			<AccordionSummary
				expandIcon={<ExpandMore color="info" />}
				sx={{
					borderBottom: 1,
					borderColor: "divider",
					px: 2,
					borderRadius: 2,
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center">
					<LockIcon color="info" />
					<Stack>
						<Typography variant="body1">{t("content locked/title")}</Typography>
						<Typography variant="body2">{t("content locked/message")}</Typography>
					</Stack>
				</Stack>
			</AccordionSummary>
			<AccordionDetails>
				<Box component="ul" sx={{ m: 0, pl: 6 }}>
					{items.map((item) => (
						<Typography key={item} component="li" variant="body2" py={1.5}>
							{item}
						</Typography>
					))}
				</Box>
			</AccordionDetails>
		</Accordion>
	);
};

export default ContentLocked;
